/**
 * Parent-Teacher → Students lookup
 *
 * Resolves the logged-in parent-teacher's students via the PTA chain:
 *   auth.uid → users.id → parent_teachers.user_id → parent_teachers.id
 *   → pta_parent_teachers.parent_teacher_id → parent_teacher_assignments.id
 *   → parent_teacher_assignments.student_id → students.id
 *
 * Each `parent_teacher_assignments` row attaches the PT to exactly one
 * student (via `student_id`). The PT's student set is the union over
 * every PTA they're attached to via `pta_parent_teachers`.
 *
 * This matches the linkage already used by the backend dashboard
 * endpoints in services/api/src/routes/parent-teacher (commit b1979a4)
 * so server-rendered aggregates and client-side dropdowns / profile
 * deep-dives all see the same student set.
 */
import { useQuery } from "@tanstack/react-query";
import { queryClient } from "../queryClient";
import { supabase } from "../supabase";
import { useAuth } from "../../context/AuthContext";
import { getErrorMessage } from "@utils/helper";

export interface ParentTeacherStudentLookup {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  // enrolment fields (active or latest student_grade_levels row)
  school_year_name?: string | null;
  grade_level_name?: string | null;
  curriculum_id?: string | null;
  grade_level_id?: string | null;
  school_year_id?: string | null;
}

async function fetchMyPtStudentsLookup(profileId: string): Promise<ParentTeacherStudentLookup[]> {
  // 1. Resolve parent_teachers.id from the auth profile.
  const { data: pt, error: ptErr } = await supabase
    .from("parent_teachers")
    .select("id")
    .eq("user_id", profileId)
    .maybeSingle();
  if (ptErr) throw ptErr;
  if (!pt) return [];

  // 2. PTA ids this PT is attached to via the junction.
  const { data: ptaJunction, error: jErr } = await supabase
    .from("pta_parent_teachers")
    .select("pta_id")
    .eq("parent_teacher_id", (pt as { id: string }).id);
  if (jErr) throw jErr;
  const ptaIds = (ptaJunction ?? [])
    .map((r) => r.pta_id)
    .filter(Boolean);
  if (ptaIds.length === 0) return [];

  // 3. Each PTA row attaches one student. Embed student + enrolment so
  // we get name, grade level, school year, and curriculum_id in one round trip.
  const { data: ptas, error: aErr } = await supabase
    .from("parent_teacher_assignments")
    .select(
      "student_id, students(id, first_name, last_name, email, grade_level:student_grade_levels(active, updated_at, curriculum_id, grade_level_id, school_year_id, grade_level:grade_levels(grade_level), schoolYear:school_years(name)))",
    )
    .in("id", ptaIds);
  if (aErr) throw aErr;

  // De-dupe in case a student has multiple PTAs (different grade
  // levels / school years) that all map to the same PT.
  const seen = new Set<string>();
  const mapped: ParentTeacherStudentLookup[] = [];
  for (const row of ptas ?? []) {
    // `students` is a to-one embed; PostgREST may type it as an array.
    const s = Array.isArray((row as any).students)
      ? (row as any).students[0]
      : (row as any).students;
    if (!s || seen.has(s.id)) continue;
    seen.add(s.id);

    // Pick the active enrolment; fall back to most-recently-updated.
    const sglRaw = (s as any).grade_level;
    const sgls: any[] = Array.isArray(sglRaw) ? sglRaw : sglRaw ? [sglRaw] : [];
    const sorted = [...sgls].sort((a, b) => {
      if (a.active && !b.active) return -1;
      if (!a.active && b.active) return 1;
      return (b.updated_at ?? "") > (a.updated_at ?? "") ? 1 : -1;
    });
    const enrolment = sorted[0] ?? null;
    const gradeLevel = Array.isArray(enrolment?.grade_level)
      ? enrolment.grade_level[0]
      : enrolment?.grade_level;
    const schoolYear = Array.isArray(enrolment?.schoolYear)
      ? enrolment.schoolYear[0]
      : enrolment?.schoolYear;

    mapped.push({
      id: s.id,
      first_name: s.first_name ?? null,
      last_name: s.last_name ?? null,
      email: s.email ?? null,
      school_year_name: (schoolYear as any)?.name ?? null,
      grade_level_name: (gradeLevel as any)?.grade_level ?? null,
      curriculum_id: enrolment?.curriculum_id ?? null,
      grade_level_id: enrolment?.grade_level_id ?? null,
      school_year_id: enrolment?.school_year_id ?? null,
    });
  }
  return mapped;
}

const NO_ROWS: ParentTeacherStudentLookup[] = [];

export const ptCaseloadQueryKey = (profileId: string | null) => ["parentTeacher", "caseload", profileId];

export function useGetMyPtStudentsLookup() {
  const { profile, session, loading: authLoading } = useAuth();
  const profileId = profile?.id ?? null;
  const sessionUserId = session?.user?.id;
  const query = useQuery({
    queryKey: ptCaseloadQueryKey(profileId),
    queryFn: () => fetchMyPtStudentsLookup(profileId ?? ""),
    enabled: profileId != null,
    retry: false,
  }, queryClient);

  if (!profileId) {
    return { rows: NO_ROWS, loading: authLoading || !!sessionUserId, error: null, refetch: query.refetch };
  }

  return {
    rows: query.data ?? NO_ROWS,
    loading: query.isPending,
    error: query.isError ? getErrorMessage(query.error, 'Failed to load students') : null,
    refetch: query.refetch,
  };
}
